import {
  useCustomMutation,
  useInvalidate,
  useCan,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Modal } from 'antd';

const { confirm } = Modal;

interface UseOAuthTokenActionsProps {
  tokenId?: string | number;
}

interface OAuthTokenAction {
  mutate: () => void;
  isLoading: boolean;
  canExecute: boolean;
}

interface OAuthTokenActions {
  revoke: OAuthTokenAction;
}

/**
 * Custom hook for OAuth token management actions
 * Provides reusable token actions with permissions, mutations, and notifications
 */
export const useOAuthTokenActions = ({
  tokenId,
}: UseOAuthTokenActionsProps): OAuthTokenActions => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { open } = useNotification();

  // Permission check
  const { data: canRevoke } = useCan({
    resource: 'iam.oauth_tokens',
    action: 'revoke',
    params: { id: tokenId },
  });

  // Mutation
  const {
    mutate: revokeToken,
    mutation: { isPending: isRevoking },
  } = useCustomMutation();

  // Action handler
  const handleRevoke = () => {
    if (!tokenId) return;

    confirm({
      title: t('iam.oauth_tokens.actions.revoke_confirm'),
      onOk: () => {
        revokeToken(
          {
            url: `iam/oauth_tokens/${tokenId}/revoke`,
            method: 'patch',
            values: {},
          },
          {
            onSuccess: () => {
              invalidate({
                resource: 'iam.oauth_tokens',
                invalidates: ['detail', 'list'],
                id: tokenId,
              });
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('iam.oauth_tokens.actions.revoke_success'),
              });
            },
          }
        );
      },
    });
  };

  return {
    revoke: {
      mutate: handleRevoke,
      isLoading: isRevoking,
      canExecute: canRevoke?.can ?? false,
    },
  };
};
